import React, { useState, useEffect } from 'react';
import { 
  Box, Text, VStack, HStack, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Spinner, Alert, AlertIcon, Button, useToast, Tabs, TabList, TabPanels, Tab, TabPanel,
  Select, Badge, Icon, Progress
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiArrowLeft, FiPackage, FiAlertTriangle, FiTrendingUp, FiActivity } from 'react-icons/fi';
import ClientIssuesTable from './ClientIssuesTable';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';


export default function ClientDashboard({ client, onClose }) {
  const [clientMetrics, setClientMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  
  const toast = useToast();
  const { makeRequest } = useAnalyticsAPI();

  const fetchClientMetrics = async () => {
    try {
      setLoading(true);
      
      // Fetch data from individual metrics API and filter for this client
      const result = await makeRequest('/api/bugs/analytics/individual-metrics', {});
      
      if (result.status === 'success' && result.data?.metrics) {
        const metricsData = result.data.metrics;
        
        // Find client data in issuesPerClient array
        const clientData = metricsData.issuesPerClient?.find(c => c.client === client.name);
        
        if (clientData && clientData.issues) {
          // Calculate module breakdown
          const moduleBreakdown = {};
          clientData.issues.forEach(issue => {
            if (!moduleBreakdown[issue.module]) {
              moduleBreakdown[issue.module] = 0;
            }
            moduleBreakdown[issue.module]++;
          });
          
          const modulesBreakdown = Object.entries(moduleBreakdown).map(([module, count]) => ({
            module,
            count,
            percentage: (count / clientData.issues.length) * 100
          }));
          
          // Calculate severity distribution
          const severityBreakdown = {
            'Critical': 0,
            'High': 0,
            'Medium': 0,
            'Low': 0
          };
          
          clientData.issues.forEach(issue => {
            const severity = issue.severity === 'HIGH' ? 'High' : 
                           issue.severity === 'MEDIUM' ? 'Medium' : 
                           issue.severity === 'LOW' ? 'Low' : 'Critical';
            severityBreakdown[severity]++;
          });
          
          const severityDistribution = [
            { name: 'Critical', value: severityBreakdown['Critical'], color: '#E53E3E' },
            { name: 'High', value: severityBreakdown['High'], color: '#DD6B20' },
            { name: 'Medium', value: severityBreakdown['Medium'], color: '#D69E2E' },
            { name: 'Low', value: severityBreakdown['Low'], color: '#38A169' }
          ];
          
          // Generate timeline (simplified)
          const timeline = [
            { month: 'Jan', issues: Math.floor(clientData.issues.length * 0.1) },
            { month: 'Feb', issues: Math.floor(clientData.issues.length * 0.15) },
            { month: 'Mar', issues: Math.floor(clientData.issues.length * 0.12) },
            { month: 'Apr', issues: Math.floor(clientData.issues.length * 0.18) },
            { month: 'May', issues: Math.floor(clientData.issues.length * 0.2) },
            { month: 'Jun', issues: Math.floor(clientData.issues.length * 0.25) }
          ];
          
          const processedMetrics = {
            client: client.name,
            totalIssues: clientData.issues.length,
            modulesBreakdown,
            severityDistribution,
            recurrencePattern: {
              description: `Analysis shows issue patterns for ${client.name}. Issues are distributed across ${modulesBreakdown.length} modules with varying severity levels.`,
              riskLevel: severityBreakdown['Critical'] > 0 ? 'High' : severityBreakdown['High'] > 0 ? 'Medium' : 'Low',
              timeline
            },
            issues: clientData.issues.map(issue => ({
              issueKey: issue.issueKey,
              severity: issue.severity === 'HIGH' ? 'High' : 
                       issue.severity === 'MEDIUM' ? 'Medium' : 
                       issue.severity === 'LOW' ? 'Low' : 'Critical',
              module: issue.module,
              rootCause: issue.rootCause,
              status: "In Progress", // Default since not in API
              title: `${issue.issueKey}: ${issue.rootCause} - ${issue.module}`,
              assignee: "Unassigned", // Default since not in API
              createdAt: new Date().toISOString() // Default since not in API
            }))
          };
          
          setClientMetrics(processedMetrics);
        } else {
          // No issues found for this client
          setClientMetrics({
            client: client.name,
            totalIssues: 0,
            modulesBreakdown: [],
            severityDistribution: [
              { name: 'Critical', value: 0, color: '#E53E3E' },
              { name: 'High', value: 0, color: '#DD6B20' },
              { name: 'Medium', value: 0, color: '#D69E2E' },
              { name: 'Low', value: 0, color: '#38A169' }
            ],
            recurrencePattern: {
              description: `No issues found for ${client.name}.`,
              riskLevel: 'Low',
              timeline: []
            },
            issues: []
          });
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch client metrics:', error);
      toast({
        title: "Error",
        description: `Failed to load client metrics: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (client) {
      fetchClientMetrics();
    }
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps



  const getRiskColor = (risk) => {
    switch (risk.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading client dashboard...</Text>
      </VStack>
    );
  }

  if (!clientMetrics) {
    return (
      <Alert status="error">
        <AlertIcon />
        <Text>Failed to load client metrics</Text>
      </Alert>
    );
  }

  const filteredIssues = clientMetrics.issues.filter(issue => {
    const matchesSeverity = !selectedSeverity || issue.severity === selectedSeverity;
    const matchesModule = !selectedModule || issue.module === selectedModule;
    return matchesSeverity && matchesModule;
  });

  const modules = [...new Set(clientMetrics.issues.map(issue => issue.module))];
  const severities = [...new Set(clientMetrics.issues.map(issue => issue.severity))];

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack>
        <Button
          leftIcon={<Icon as={FiArrowLeft} />}
          variant="ghost"
          onClick={onClose}
        >
          Back to Clients
        </Button>
      </HStack>

      {/* Client Header */}
      <Card bg="gradient(linear, to-r, blue.500, purple.600)" color="white">
        <CardBody>
          <VStack align="start" spacing={2}>
            <Text fontSize="2xl" fontWeight="bold">{clientMetrics.client}</Text>
            <HStack spacing={4}>
              <Badge bg="whiteAlpha.300" color="white" px={3} py={1}>
                {clientMetrics.totalIssues} Total Issues
              </Badge>
              <Badge 
                bg={`${getRiskColor(clientMetrics.recurrencePattern.riskLevel)}.500`} 
                color="white" 
                px={3} 
                py={1}
              >
                {clientMetrics.recurrencePattern.riskLevel} Risk
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Tabs */}
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Modules</Tab>
          <Tab>Issues</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel p={0}>
            <VStack spacing={6} align="stretch">
              {/* Metrics Grid */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Total Issues</StatLabel>
                      <StatNumber color="blue.500">{clientMetrics.totalIssues}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiActivity} mr={1} />
                        Active tracking
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Modules Affected</StatLabel>
                      <StatNumber color="purple.500">{clientMetrics.modulesBreakdown.length}</StatNumber>
                      <StatHelpText>
                        <Icon as={FiPackage} mr={1} />
                        System modules
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Critical/High</StatLabel>
                      <StatNumber color="red.500">
                        {clientMetrics.severityDistribution.find(s => s.name === 'Critical')?.value + 
                         clientMetrics.severityDistribution.find(s => s.name === 'High')?.value}
                      </StatNumber>
                      <StatHelpText>
                        <Icon as={FiAlertTriangle} mr={1} />
                        Priority issues
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
                
                <Card>
                  <CardBody>
                    <Stat>
                      <StatLabel>Risk Level</StatLabel>
                      <StatNumber color={`${getRiskColor(clientMetrics.recurrencePattern.riskLevel)}.500`}>
                        {clientMetrics.recurrencePattern.riskLevel}
                      </StatNumber>
                      <StatHelpText>
                        <Icon as={FiTrendingUp} mr={1} />
                        Recurrence risk
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Charts Row */}
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                {/* Severity Distribution Pie Chart */}
                <Card>
                  <CardBody>
                    <Text fontWeight="bold" mb={4}>Severity Distribution</Text>
                    <Box h="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={clientMetrics.severityDistribution.filter(item => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {clientMetrics.severityDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>

                {/* Recurrence Pattern */}
                <Card>
                  <CardBody>
                    <Text fontWeight="bold" mb={4}>Issue Timeline (6 Months)</Text>
                    <Box h="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={clientMetrics.recurrencePattern.timeline}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="issues" fill="#3182CE" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Recurrence Pattern Description */}
              <Card>
                <CardBody>
                  <HStack mb={3}>
                    <Icon as={FiTrendingUp} color="orange.500" />
                    <Text fontWeight="bold">Recurrence Pattern Analysis</Text>
                  </HStack>
                  <Text color="gray.700" lineHeight="1.6">
                    {clientMetrics.recurrencePattern.description}
                  </Text>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* Modules Tab */}
          <TabPanel p={0}>
            <Card>
              <CardBody>
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FiPackage} color="blue.500" />
                    <Text fontSize="xl" fontWeight="bold">Modules Breakdown</Text>
                  </HStack>
                  
                  <Box h="400px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={clientMetrics.modulesBreakdown} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="module" type="category" width={120} />
                        <RechartsTooltip 
                          formatter={(value, name) => [value, 'Issues']}
                          labelFormatter={(label) => `Module: ${label}`}
                        />
                        <Bar dataKey="count" fill="#3182CE" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <VStack spacing={3} align="stretch">
                    {clientMetrics.modulesBreakdown.map((module, index) => (
                      <Box key={module.module}>
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="medium">{module.module}</Text>
                          <Text fontSize="sm" color="gray.600">{module.count} issues</Text>
                        </HStack>
                        <Progress 
                          value={module.percentage} 
                          size="lg" 
                          colorScheme="blue"
                          borderRadius="md"
                        />
                      </Box>
                    ))}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Issues Tab */}
          <TabPanel p={0}>
            <VStack spacing={4} align="stretch">
              {/* Filters */}
              <Card>
                <CardBody>
                  <HStack spacing={4} wrap="wrap">
                    <Select
                      placeholder="All Severities"
                      value={selectedSeverity}
                      onChange={(e) => setSelectedSeverity(e.target.value)}
                      maxW="200px"
                    >
                      {severities.map(severity => (
                        <option key={severity} value={severity}>{severity}</option>
                      ))}
                    </Select>
                    
                    <Select
                      placeholder="All Modules"
                      value={selectedModule}
                      onChange={(e) => setSelectedModule(e.target.value)}
                      maxW="200px"
                    >
                      {modules.map(module => (
                        <option key={module} value={module}>{module}</option>
                      ))}
                    </Select>
                    
                    <Badge colorScheme="blue" px={3} py={1}>
                      {filteredIssues.length} issues
                    </Badge>
                  </HStack>
                </CardBody>
              </Card>

              {/* Issues Table */}
              <ClientIssuesTable issues={filteredIssues} />
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
